import {AfterViewInit, Component, EventEmitter, Input, Optional, Output, ViewEncapsulation} from '@angular/core';
import {KEYS} from '../../../translation-keys';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {ConfigService} from '@ngmedax/config';
import {Questionnaire, Workflow, Translation} from '@ngmedax/common-questionnaire-types';
import {TRANSLATION_DEFAULT_SCOPE} from '../../../constants';
import {QuestionnaireSelectService} from '../services/questionnaire-select.service';
import {DragulaService} from 'ng2-dragula';
import {QuestionnaireGroup} from '../../../types';
declare const $: any;


// hack to inject decorator declarations. must occur before class declaration!
export interface QuestionnaireSelectComponent extends Translatable {}

@Component({
  selector: 'app-questionnaire-select',
  templateUrl: './questionnaire-select.component.html',
  styleUrls: ['./questionnaire-select.component.css'],
  encapsulation: ViewEncapsulation.None
})
@Translatable({scope: TRANSLATION_DEFAULT_SCOPE, keys: KEYS})
export class QuestionnaireSelectComponent implements AfterViewInit {
  /**
   * Hardcoded to "de_DE" for now.
   * We need to change this, when we implement multi language support
   * @type {string}
   */
  public locale = 'de_DE';

  /**
   * Available questionnaires
   * @type {Questionnaire[]} questionnaires
   */
  public questionnaires: Questionnaire[] = [];

  /**
   * Available workflows
   * @type {Workflow[]} workflows
   */
  public workflows: Workflow[] = [];

  /**
   * Available questionnaire groups
   * @type {QuestionnaireGroup[]} groups
   */
  public groups: QuestionnaireGroup[] = [];

  /**
   * Currently selected questionnaire
   * @type {string} choice
   */
  public choice: string = null;

  /**
   * Currently selected workflow
   * @type {string} workflowChoice
   */
  public workflowChoice: string = null;

  /**
   * Options for questionnaire select2
   */
  public options: any = {width: '100%', placeholder: '...',};

  /**
   * Options for group select 2
   */
  public groupOptions: any = {width: '100%', placeholder: '...',};

  /**
   * Mapping of questionnaire ids to questionnaire titles
   * @type {{[id: string]: Translation}}
   */
  public questionnaireTitles: {[id: string]: Translation} = {};

  /**
   * Mapping of questionnaire ids to thieme flag (meta.source.provider === 'thieme')
   * @type {{[id: string]: boolean}}
   */
  public thiemeIds: {[id: string]: boolean} = {};

  /**
   * Questionnaires matching the current origin filter (rendered in the dropdown)
   * @type {Questionnaire[]}
   */
  public filteredQuestionnaires: Questionnaire[] = [];

  /**
   * Origin filter for the questionnaire dropdown: 'all', 'mymedax' or 'thieme'
   * @type {string}
   */
  public originFilter = 'all';

  /**
   * True if at least one thieme questionnaire is available (shows the origin filter)
   * @type {boolean}
   */
  public hasThieme = false;

  /**
   * Map of questionnaire id to the full questionnaire (for the origin renderer)
   * @type {{[id: string]: Questionnaire}}
   */
  public questionnairesById: {[id: string]: Questionnaire} = {};

  /**
   * Mapping of workflow ids to workflow titles
   * @type {{[id: string]: Translation}}
   */
  public workflowTitles: {[id: string]: Translation} = {};

  /**
   * Currently selected questionnaire ids. You can use this for two-way data binding
   * @type {string[]} selected
   */
  @Input() public selected: string[] = [];

  /**
   * Currently selected workflow id
   * @type {string} selectedWorkflowId
   */
  @Input() public selectedWorkflowId: string = null;

  /**
   * Enables to select questionnaire groups
   */
  @Input() public enableGroups: boolean = false;

  /**
   * Enables to select workflows
   */
  @Input() public enableWorkflows: boolean = false;

  /**
   * Event emitter for when selected questionnaire ids changed. You can use this for two-way data binding
   * @type {EventEmitter<string[]>} selectedChange
   */
  @Output() public selectedChange: EventEmitter<string[]> = new EventEmitter<string[]>();

  /**
   * Event emitter for when a workflow is selected
   * @type {EventEmitter<string>} selectWorkflow
   */
  @Output() public selectWorkflow: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Injects dependencies
   * Loads questionnaires
   */
  public constructor(
    @Optional() private translationService: TranslationService,
    @Optional() private configService: ConfigService,
    private selectService: QuestionnaireSelectService,
    private dragula: DragulaService,
  ) {
    /**
     * Bugfix for dragula: Deletes previous elements bag on progressive page reloads
     */
    for (const bag of ['questionnaire-list-bag']) {
      this.dragula.find(bag) && this.dragula.destroy(bag);
    }

    // only allow dragging via elements that contain the class: qa-handle-control
    this.dragula.createGroup('questionnaire-list-bag', {
      moves: (el, container, handle) => handle.classList.contains('qa-handle-control')
    });

    this.initData().catch(console.error);
  }

  public ngAfterViewInit() {
    // change select2 placeholders when group select is enabled
    if (this.enableGroups) {
      this.selectService.loadQuestionnaireGroups()
        .then(groups => {
          this.groups = groups;
        })
        .catch(error => {
          console.error(error);
          alert(this._(KEYS.DEFAULT.ERROR_LOADING_GROUPS));
        });
    }

    if (this.selectedWorkflowId) {
      this.workflowChoice = this.selectedWorkflowId;
    }
  }

  /**
   * Handler for when questionnaire added to list
   *
   * @param {string} id
   */
  public onAddQuestionnaire(id: string) {
    this.selected.indexOf(id) === -1 && this.selected.push(id);
    this.selectedChange.emit(this.selected);
    this.choice = null;
  }

  /**
   * Handler for when questionnaire group added to list
   *
   * @param {string} id
   */
  public onAddGroup(id: string) {
    const group = this.groups.filter(group => group.uid === id).pop() || {questionnaires: []};
    (group.questionnaires || []).forEach(q => this.onAddQuestionnaire(q.id));
  }

  public onSelectWorkflow(id: string) {
    this.selected = [];
    this.selectWorkflow.emit(id);

    if (!id) {
      this.selectedWorkflowId = null;
      this.selected = [];
      return;
    }

    const workflow = this.workflows.filter(w => w.id === id).pop() || {stages: []};
    const questionnaireIds: string[] = workflow.stages.reduce((acc, stage) => (<any>acc).concat(stage.questionnaires), <any>[]);
    questionnaireIds.forEach(q => this.onAddQuestionnaire(q));
    this.selectedWorkflowId = id;
  }

  /**
   * Handler for when questionnaire position changes in list
   *
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  public onPositionChange(currentPosition: number, newPosition: number) {
    // early bailout when changing positions does not make sense
    if (currentPosition >= (this.selected.length) || newPosition < 0) {
      return;
    }

    // reorder answers by current and new position
    this.selected.splice(newPosition, 0, this.selected.splice(currentPosition, 1)[0]);
    this.selectedChange.emit(this.selected);
  }

  /**
   * Handler for when questionnaire is deleted from list
   *
   * @param {number} position
   */
  public onDelete(position: number) {
    this.selected.splice(position, 1);
    this.selectedChange.emit(this.selected);
  }

  /**
   * Returns true if given questionnaire id belongs to a thieme questionnaire
   *
   * @param {string} id
   * @returns {boolean}
   */
  public isThieme(id: string): boolean {
    return !!this.thiemeIds[id];
  }

  /**
   * Returns true if the current selection contains at least one thieme questionnaire
   *
   * @returns {boolean}
   */
  public hasThiemeSelected(): boolean {
    return this.selected.filter(id => this.isThieme(id)).length > 0;
  }

  /**
   * Returns the dropdown option text for given questionnaire. Thieme questionnaires get
   * a plain text suffix, as select2 option elements can not render html badges.
   *
   * @param {Questionnaire} questionnaire
   * @returns {string}
   */
  public getOptionText(questionnaire: Questionnaire): string {
    const title = questionnaire.meta.title[this.locale] || '';
    return this.thiemeLicensed && this.isThieme(questionnaire.id) ? `${title} [${this._(KEYS.DEFAULT.THIEME)}]` : title;
  }

  /**
   * True when the thieme feature is licensed (origin ui only shown then)
   */
  public get thiemeLicensed(): boolean {
    return !!this.configService && this.configService.get('feature.thieme') === true;
  }

  /**
   * Returns the full questionnaire object for a selected id (for the origin renderer)
   */
  public getQuestionnaire(id: string): Questionnaire {
    return this.questionnairesById[id];
  }

  /**
   * Returns the display name for a selected questionnaire. For licensed thieme
   * bogen the "(Code / Locale)" prefix is dropped (the code is shown in the pill).
   *
   * @param {string} id
   * @returns {string}
   */
  public getDisplayName(id: string): string {
    const titles = this.questionnaireTitles[id] || {};
    const title = titles[this.locale] || '';

    if (this.thiemeLicensed && this.isThieme(id)) {
      const match = /^\(\s*.+?\s*\/\s*[a-zA-Z]{2}_[a-zA-Z]{2}\s*\)\s*(.*)$/.exec(title);
      if (match) {
        return match[1];
      }
    }

    return title;
  }

  /**
   * Handler for when the origin filter for the dropdown changes
   *
   * @param {string} filter
   */
  public onOriginFilter(filter: string) {
    this.originFilter = filter;
    this.applyOriginFilter();
  }

  /**
   * Applies the current origin filter to the dropdown questionnaire list
   */
  private applyOriginFilter() {
    if (this.originFilter === 'thieme') {
      this.filteredQuestionnaires = this.questionnaires.filter(q => this.isThieme(q.id));
      return;
    }

    if (this.originFilter === 'mymedax') {
      this.filteredQuestionnaires = this.questionnaires.filter(q => !this.isThieme(q.id));
      return;
    }

    this.filteredQuestionnaires = this.questionnaires;
  }

  private async initData(): Promise<void> {
    try {
      this.questionnaires = await this.selectService.loadQuestionnaires();
      this.questionnaires.forEach(q => {
        this.questionnaireTitles[q.id] = q.meta.title;
        this.questionnairesById[q.id] = q;
        const meta: any = q.meta || {};
        this.thiemeIds[q.id] = !!(meta.source && meta.source.provider === 'thieme');
      });
      this.hasThieme = this.questionnaires.some(q => this.isThieme(q.id));
      this.applyOriginFilter();
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.DEFAULT.ERROR_LOADING_QUESTIONNAIRES));
    }

    try {
      this.workflows = await this.selectService.loadWorkflows();
      this.workflows.forEach(w => this.workflowTitles[w.id] = w.name);
    } catch (error) {
      console.error(error);
      alert(this._(KEYS.DEFAULT.ERROR_LOADING_WORKFLOWS));
    }
  }
}
